import { lazy, Suspense } from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';

import Loader from 'shared/Loader';

import { useMyPrograms } from 'hooks/useMyPrograms/useMyPrograms';
import { PROGRAM_CONFIGS } from 'utils/constants';
import { lazyRetry } from 'utils/lazy';
import { PathName } from './paths';

const CreateAppointment = lazy(() => lazyRetry(() => import('containers/CreateAppointment')));
const Appointment = lazy(() => lazyRetry(() => import('pages/Appointment')));
const AppointmentRequired = lazy(() => lazyRetry(() => import('pages/AppointmentRequired')));
const Care = lazy(() => lazyRetry(() => import('pages/GetCare')));
const ZoomCall = lazy(() => lazyRetry(() => import('pages/ZoomCall')));

const RedirectToCreateAppointment = () => {
  const location = useLocation();
  return (
    <Navigate to={{ pathname: PathName.CreateAppointment, search: location.search }} replace />
  );
};

const AppointmentRequiredGuard = () => {
  const { activePrograms } = useMyPrograms();
  const available = PROGRAM_CONFIGS.some(({ category }) =>
    activePrograms.some(({ program }) => program.category === category)
  );

  if (!available) {
    return <Navigate to={PathName.NotFound} replace />;
  }

  return (
    <Suspense fallback={<Loader isVisible />}>
      <AppointmentRequired />
    </Suspense>
  );
};

export const getTopLevelAppointmentRoutes = () => (
  <>
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <CreateAppointment />
        </Suspense>
      }
      path={PathName.CreateAppointment}
    />
    <Route element={<RedirectToCreateAppointment />} path="/create-appointment-extended" />
    <Route
      element={<RedirectToCreateAppointment />}
      path="appointments/create-appointment-extended"
    />
  </>
);

export const getMainLayoutAppointmentRoutes = () => (
  <>
    <Route element={<Care />} path={PathName.GetCare} />
    <Route element={<ZoomCall />} path={PathName.ZoomCall} />
    <Route element={<Navigate to={PathName.GetCare} replace />} path="/appointments" />
    <Route element={<Navigate to={PathName.GetCare} replace />} path="/appointments/*" />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <Appointment />
        </Suspense>
      }
      path={`${PathName.Appointment}/:id`}
    />
  </>
);

export const getProtectedAppointmentRoutes = () => (
  <>
    <Route element={<AppointmentRequiredGuard />} path={PathName.AppointmentRequired} />
  </>
);
